// <DeityDaily/> — the SyQi HOME daily check-in strip. Replaces the old swap-
// scale daily widget with five ORDER-deity placards (one per gear anchor), each
// showing the deity's icon, its acquisition points (X/400, unit label from gameSettings), and a circle per
// active principle — filled green once that principle is marked done today.
// Tap a circle to log/unlog today's completion right from home.
//
// A SHAPE row sits below, revealed once SHAPE unlocks. The deity roster for
// SHAPE isn't locked yet, so it renders placeholder shells for now — the
// underlying store (principleProgress jsonb, keyed by tool id) already holds
// any future SHAPE deity with no schema change.
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../v10/theme';
import { GlassCard, Medallion } from '../v10/ui';
import { portraitFor } from '../v10/roster';
import { GEAR_ANCHOR, type GearId } from './ascension';
import { getTool, type DmtToolId } from './spectrum-tools';
import { deityLevelData } from './deity-data';
import { computeProgress, todayKey, type ProgressStats } from './deity-progress';
import { POINTS_UNIT } from './gameSettings';
import { useCompassConfig } from './sectorConfig';

const GEAR_ORDER: GearId[] = ['orient', 'regulate', 'direct', 'execute', 'revise'];
const ORDER_DEITIES: DmtToolId[] = GEAR_ORDER.map((g) => GEAR_ANCHOR[g]);

function DeityPlacard({
  toolId,
  stats,
  onOpen,
  onToggle,
}: {
  toolId: DmtToolId;
  stats: ProgressStats;
  onOpen: () => void;
  onToggle: (id: string) => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const name = getTool(toolId).name;
  const hasArt = !!portraitFor(toolId);
  const picks = stats.activePicks;
  const doneCount = picks.filter((p) => stats.doneToday.includes(p)).length;
  return (
    <GlassCard style={styles.placard}>
      <Pressable style={styles.placardTop} onPress={onOpen}>
        <View style={styles.iconBox}>
          {hasArt ? <Medallion id={toolId} size={42} /> : <Text style={styles.iconMono}>{name.charAt(0)}</Text>}
        </View>
        <View style={styles.placardMeta}>
          <Text style={styles.placardName} numberOfLines={1}>{name}</Text>
          <Text style={styles.placardPts}>
            {stats.maxed ? `L${stats.level} · max` : `${stats.points}/${stats.levelTarget} ${POINTS_UNIT} · L${stats.level}`}
          </Text>
        </View>
      </Pressable>
      {picks.length > 0 ? (
        <>
          <View style={styles.circleRow}>
            {picks.map((p) => {
              const done = stats.doneToday.includes(p);
              return (
                <Pressable
                  key={p}
                  onPress={() => onToggle(p)}
                  hitSlop={6}
                  style={[styles.circle, { backgroundColor: done ? t.onPace : t.track, boxShadow: done ? `0 0 8px ${t.goldGlow}` : undefined } as object]}
                />
              );
            })}
          </View>
          <Text style={styles.todayLabel}>{doneCount}/{picks.length} today</Text>
        </>
      ) : (
        <Pressable onPress={onOpen}>
          <Text style={styles.noPicks}>set picks →</Text>
        </Pressable>
      )}
    </GlassCard>
  );
}

/** A locked placeholder used for the not-yet-rostered SHAPE row. */
function PlaceholderPlacard() {
  const styles = useStyles();
  return (
    <GlassCard style={[styles.placard, styles.placardLocked]}>
      <View style={styles.placardTop}>
        <View style={[styles.iconBox, styles.iconBoxLocked]}>
          <Text style={styles.iconMono}>🔒</Text>
        </View>
        <View style={styles.placardMeta}>
          <Text style={styles.placardName}>SHAPE</Text>
          <Text style={styles.placardPts}>—</Text>
        </View>
      </View>
    </GlassCard>
  );
}

export function DeityDaily({
  userId,
  onOpenTool,
  onEngage,
}: {
  userId: string | undefined;
  onOpenTool: (toolId: DmtToolId) => void;
  /** Logging a principle is the daily engagement for MoyRi's dot (see
   *  toolEngagement.ts); undefined when this strip renders outside a band. */
  onEngage?: () => void;
}) {
  const styles = useStyles();
  const { config, togglePrincipleDay } = useDailyConfig(userId);
  const todayStr = todayKey();

  return (
    <View style={styles.wrap}>
      <Text style={styles.rowLabel}>DAILY · ORDER</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.row}>
        {ORDER_DEITIES.map((toolId) => {
          const prog = config.principleProgress[toolId];
          const stats = computeProgress(prog, deityLevelData(toolId), todayStr, toolId, { ledger: config.ledger, game: config.game });
          return (
            <DeityPlacard
              key={toolId}
              toolId={toolId}
              stats={stats}
              onOpen={() => onOpenTool(toolId)}
              onToggle={(id) => {
                togglePrincipleDay(toolId, id, todayStr);
                onEngage?.();
              }}
            />
          );
        })}
      </ScrollView>

      {config.shapeEnabled ? (
        <>
          <Text style={styles.rowLabel}>DAILY · SHAPE</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.row}>
            {GEAR_ORDER.map((g) => (
              <PlaceholderPlacard key={g} />
            ))}
          </ScrollView>
        </>
      ) : null}
    </View>
  );
}

/** Thin wrapper around useCompassConfig exposing just what this strip needs. */
function useDailyConfig(userId: string | undefined) {
  const cc = useCompassConfig(userId);
  return { config: cc.config, togglePrincipleDay: cc.logPrincipleToday };
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    wrap: { gap: 8 },
    rowLabel: { fontFamily: DISPLAY_FONT, fontSize: 11, letterSpacing: 2.5, color: t.ivorySoft },
    row: { gap: 10, paddingBottom: 2, paddingHorizontal: 1 },
    placard: {
      width: 148,
      borderRadius: 22,
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      padding: 12,
      gap: 9,
    },
    placardLocked: { opacity: 0.5 },
    placardTop: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    iconBox: {
      width: 42,
      height: 42,
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconBoxLocked: { borderRadius: 21, backgroundColor: t.navyRaised },
    iconMono: { fontSize: 18, fontWeight: '900', color: t.gold },
    placardMeta: { flex: 1, gap: 1 },
    placardName: { fontFamily: DISPLAY_FONT, fontSize: 14, color: t.ivory, letterSpacing: 1.5 },
    placardPts: { fontSize: 12, fontWeight: '800', color: t.goldBright },
    circleRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
    circle: { width: 22, height: 22, borderRadius: 11, borderWidth: 1, borderColor: t.navyLine },
    todayLabel: { fontSize: 10, fontWeight: '700', color: t.ivorySoft },
    noPicks: { fontSize: 11, fontWeight: '700', color: t.gold, paddingVertical: 4 },
  }),
);

export default DeityDaily;
